<template>
  <page-layout
      :title="pageTitle"
      :breadcrumb="pageBreadcrumb"
      :status="pageStatus"
      :code="pageCode"
      :info-cards="pageInfoCards"
  >
    <!-- Header Actions -->
    <template #actions>
      <md-button class="md-primary md-round" @click="goToEdit(vendor.id)">
        <md-icon>edit</md-icon>
        Edit Vendor
      </md-button>

      <md-button
          v-if="can('update-vendor')"
          class="md-round"
          :class="vendor.is_active ? 'md-warning' : 'md-success'"
          @click="toggleVendorStatus"
      >
        <md-icon>{{ vendor.is_active ? 'pause' : 'play_arrow' }}</md-icon>
        {{ vendor.is_active ? 'Deactivate' : 'Activate' }}
      </md-button>

      <md-button class="md-info md-round" @click="goBack">
        <md-icon>arrow_back</md-icon>
        Back
      </md-button>
    </template>

    <template #content>
      <page-tabs>
        <!-- Overview Tab -->
        <md-tab id="tab-overview" md-label="Overview" md-icon="dashboard">
          <div class="tab-content">
            <!-- Vendor Profile Card -->
            <div class="profile-section">
              <div class="profile-card">
                <div class="profile-cover"></div>
                <div class="profile-content">
                  <div class="profile-avatar-wrapper">
                    <div class="profile-avatar">
                      <img
                          v-if="vendor.logo"
                          :src="vendorLogoUrl"
                          :alt="vendor.name"
                      />
                      <div v-else class="avatar-placeholder">
                        {{ getInitials(vendor.name) }}
                      </div>
                    </div>
                  </div>
                  <div class="profile-info">
                    <h2 class="profile-name">{{ vendor.name }}</h2>
                    <p class="profile-business">{{ vendor.busines_name || 'No business name' }}</p>
                    <div class="profile-badges">
                      <span
                          class="status-badge"
                          :class="vendor.is_active ? 'active' : 'inactive'"
                      >
                        <span class="status-dot"></span>
                        {{ vendor.is_active ? 'Active' : 'Inactive' }}
                      </span>
                      <span v-if="vendor.code" class="code-badge">
                        <md-icon>tag</md-icon>
                        {{ vendor.code }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Details Grid -->
            <div class="details-grid">
              <!-- Contact Information -->
              <div class="detail-card">
                <div class="detail-card-header">
                  <md-icon>contact_mail</md-icon>
                  <h3>Contact Information</h3>
                </div>
                <div class="detail-card-body">
                  <div class="detail-row">
                    <span class="detail-label">
                      <md-icon>email</md-icon>
                      Email
                    </span>
                    <span class="detail-value">
                      <a :href="'mailto:' + vendor.email">{{ vendor.email || 'N/A' }}</a>
                    </span>
                  </div>
                  <div class="detail-row">
                    <span class="detail-label">
                      <md-icon>phone</md-icon>
                      Phone
                    </span>
                    <span class="detail-value">
                      <a :href="'tel:' + vendor.phone_number">{{ vendor.phone_number || 'N/A' }}</a>
                    </span>
                  </div>
                  <div class="detail-row">
                    <span class="detail-label">
                      <md-icon>call</md-icon>
                      Telephone
                    </span>
                    <span class="detail-value">{{ vendor.telephone_number || 'N/A' }}</span>
                  </div>
                  <div class="detail-row">
                    <span class="detail-label">
                      <md-icon>public</md-icon>
                      Website
                    </span>
                    <span class="detail-value">
                      <a v-if="vendor.website" :href="vendor.website" target="_blank">
                        {{ vendor.website }}
                      </a>
                      <span v-else>N/A</span>
                    </span>
                  </div>
                </div>
              </div>

              <!-- Address Information -->
              <div class="detail-card">
                <div class="detail-card-header">
                  <md-icon>location_on</md-icon>
                  <h3>Address</h3>
                </div>
                <div class="detail-card-body">
                  <div class="detail-row">
                    <span class="detail-label">
                      <md-icon>home</md-icon>
                      Street
                    </span>
                    <span class="detail-value">{{ vendor.address || 'N/A' }}</span>
                  </div>
                  <div class="detail-row">
                    <span class="detail-label">
                      <md-icon>location_city</md-icon>
                      City
                    </span>
                    <span class="detail-value">{{ vendor.city || 'N/A' }}</span>
                  </div>
                  <div class="detail-row">
                    <span class="detail-label">
                      <md-icon>flag</md-icon>
                      Country
                    </span>
                    <span class="detail-value">{{ vendor.country || 'N/A' }}</span>
                  </div>
                  <div class="detail-row">
                    <span class="detail-label">
                      <md-icon>markunread_mailbox</md-icon>
                      Postal Code
                    </span>
                    <span class="detail-value">{{ vendor.postal_code || 'N/A' }}</span>
                  </div>
                </div>
              </div>

              <!-- Business Information -->
              <div class="detail-card">
                <div class="detail-card-header">
                  <md-icon>business</md-icon>
                  <h3>Business Information</h3>
                </div>
                <div class="detail-card-body">
                  <div class="detail-row">
                    <span class="detail-label">
                      <md-icon>fingerprint</md-icon>
                      Tax ID (TIN)
                    </span>
                    <span class="detail-value">{{ vendor.tin_number || 'N/A' }}</span>
                  </div>
                  <div class="detail-row">
                    <span class="detail-label">
                      <md-icon>verified</md-icon>
                      Registration No.
                    </span>
                    <span class="detail-value">{{ vendor.registration_number || 'N/A' }}</span>
                  </div>
                  <div class="detail-row">
                    <span class="detail-label">
                      <md-icon>category</md-icon>
                      Business Type
                    </span>
                    <span class="detail-value">{{ vendor.business_type || 'N/A' }}</span>
                  </div>
                  <div class="detail-row">
                    <span class="detail-label">
                      <md-icon>event</md-icon>
                      Established
                    </span>
                    <span class="detail-value">{{ formatDate(vendor.established_at) }}</span>
                  </div>
                </div>
              </div>

              <!-- Statistics -->
              <div class="detail-card">
                <div class="detail-card-header">
                  <md-icon>bar_chart</md-icon>
                  <h3>Statistics</h3>
                </div>
                <div class="detail-card-body">
                  <div class="stat-row">
                    <div class="stat-item">
                      <span class="stat-value">{{ stats.labourers || 0 }}</span>
                      <span class="stat-label">Labourers</span>
                    </div>
                    <div class="stat-item">
                      <span class="stat-value">{{ stats.guidances || 0 }}</span>
                      <span class="stat-label">Guidances</span>
                    </div>
                  </div>
                  <div class="stat-row">
                    <div class="stat-item">
                      <span class="stat-value">{{ stats.active_assignments || 0 }}</span>
                      <span class="stat-label">Active</span>
                    </div>
                    <div class="stat-item">
                      <span class="stat-value">{{ formatNumber(stats.total_revenue || 0) }}</span>
                      <span class="stat-label">Revenue</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Description -->
            <div class="description-card" v-if="vendor.description">
              <div class="detail-card-header">
                <md-icon>description</md-icon>
                <h3>About</h3>
              </div>
              <div class="detail-card-body">
                <p class="description-text" v-html="vendor.description"></p>
              </div>
            </div>
          </div>
        </md-tab>

        <!-- Labourers Tab -->
        <md-tab id="tab-labourers" md-label="Labourers" md-icon="people">
          <div class="tab-content">
            <data-table
                title="Vendor Labourers"
                :columns="labourerColumns"
                :data="labourers"
                :total="labourersTotal"
                :searchable="true"
                search-placeholder="Search labourers..."
                @search="onLabourerSearch"
            >
              <template #cell-name="{ item }">
                <clickable-link
                    :id="item.id"
                    resource="Labourer"
                    :text="item.full_name"
                    :stop-propagation="true"
                />
              </template>

              <template #cell-status="{ item }">
                <badge :type="getLabourerStatusBadge(item.status)">
                  {{ item.status || 'Unknown' }}
                </badge>
              </template>

              <template #actions="{ item }">
                <md-button
                    class="md-icon-button md-primary"
                    @click="viewLabourer(item.id)"
                    title="View Labourer"
                >
                  <md-icon>visibility</md-icon>
                </md-button>
              </template>

              <template #empty-actions>
                <p class="text-muted">No labourers assigned to this vendor</p>
              </template>
            </data-table>
          </div>
        </md-tab>

        <!-- Guidances Tab -->
        <md-tab id="tab-guidances" md-label="Guidances" md-icon="assignment">
          <div class="tab-content">
            <data-table
                title="Vendor Guidances"
                :columns="guidanceColumns"
                :data="guidances"
                :total="guidancesTotal"
                :searchable="true"
                search-placeholder="Search guidances..."
                @search="onGuidanceSearch"
            >
              <template #cell-guidance_number="{ item }">
                <clickable-link
                    :id="item.id"
                    resource="Guidance"
                    :text="item.guidance_number"
                    :stop-propagation="true"
                />
              </template>

              <template #cell-status="{ item }">
                <badge :type="getGuidanceStatusBadge(item.status)">
                  {{ item.status || 'Draft' }}
                </badge>
              </template>

              <template #actions="{ item }">
                <md-button
                    class="md-icon-button md-primary"
                    @click="viewGuidance(item.id)"
                    title="View Guidance"
                >
                  <md-icon>visibility</md-icon>
                </md-button>
              </template>

              <template #empty-actions>
                <p class="text-muted">No guidances for this vendor</p>
              </template>
            </data-table>
          </div>
        </md-tab>

        <!-- Documents Tab -->
        <md-tab id="tab-documents" md-label="Documents" md-icon="folder">
          <div class="tab-content">
            <div class="documents-section">
              <div class="documents-header">
                <h4>Documents</h4>
                <md-button class="md-primary md-round" @click="triggerFileUpload">
                  <md-icon>cloud_upload</md-icon>
                  Upload Document
                </md-button>
                <input
                    ref="fileInput"
                    type="file"
                    style="display: none"
                    @change="onFileChange"
                    accept="image/*,application/pdf"
                />
              </div>

              <div v-if="documents && documents.length > 0" class="documents-grid">
                <div
                    v-for="doc in documents"
                    :key="doc.id"
                    class="document-card"
                >
                  <div class="document-icon">
                    <md-icon>{{ getDocumentIcon(doc.file_type) }}</md-icon>
                  </div>
                  <div class="document-info">
                    <span class="document-name">{{ doc.name }}</span>
                    <span class="document-meta">{{ doc.file_type }} • {{ formatFileSize(doc.size) }}</span>
                  </div>
                  <div class="document-actions">
                    <md-button
                        class="md-icon-button md-primary"
                        @click="previewDocument(doc)"
                    >
                      <md-icon>visibility</md-icon>
                    </md-button>
                    <md-button
                        class="md-icon-button md-danger"
                        @click="deleteDocument(doc)"
                    >
                      <md-icon>delete</md-icon>
                    </md-button>
                  </div>
                </div>
              </div>

              <div v-else class="empty-state">
                <md-icon>folder_open</md-icon>
                <p>No documents uploaded yet</p>
              </div>
            </div>
          </div>
        </md-tab>

        <!-- Activity Tab -->
        <md-tab id="tab-activity" md-label="Activity" md-icon="history">
          <div class="tab-content">
            <div class="activity-timeline">
              <div
                  v-for="activity in activities"
                  :key="activity.id"
                  class="timeline-item"
              >
                <div class="timeline-marker" :class="activity.type">
                  <md-icon>{{ getActivityIcon(activity.type) }}</md-icon>
                </div>
                <div class="timeline-content">
                  <div class="timeline-title">{{ activity.title }}</div>
                  <div class="timeline-description">{{ activity.description }}</div>
                  <div class="timeline-meta">
                    <span class="timeline-time">
                      <md-icon>schedule</md-icon>
                      {{ formatDateTime(activity.created_at) }}
                    </span>
                    <span class="timeline-user" v-if="activity.user">
                      <md-icon>person</md-icon>
                      {{ activity.user }}
                    </span>
                  </div>
                </div>
              </div>

              <div v-if="!activities || activities.length === 0" class="empty-state">
                <md-icon>history</md-icon>
                <p>No activity recorded</p>
              </div>
            </div>
          </div>
        </md-tab>
      </page-tabs>
    </template>
  </page-layout>
</template>

<script>
import PageLayout from '@/components/Layouts/PageLayout.vue';
import PageTabs from '@/components/Layouts/PageTabs.vue';
import DataTable from '@/components/DataTable/DataTable.vue';
import ClickableLink from '@/components/ClickableLink.vue';
import Badge from '@/components/Badge.vue';
import PageMixin from '@/mixins/page-mixin';
import Swal from 'sweetalert2';
import moment from 'moment';

export default {
  name: 'ViewVendorPage',
  components: {
    PageLayout,
    PageTabs,
    DataTable,
    ClickableLink,
    Badge
  },
  mixins: [PageMixin],
  data: function() {
    return {
      vendor: {
        id: null,
        name: null,
        busines_name: null,
        email: null,
        phone_number: null,
        telephone_number: null,
        website: null,
        address: null,
        city: null,
        country: null,
        postal_code: null,
        tin_number: null,
        registration_number: null,
        business_type: null,
        established_at: null,
        description: null,
        logo: null,
        code: null,
        is_active: false
      },
      stats: {
        labourers: 0,
        guidances: 0,
        active_assignments: 0,
        total_revenue: 0
      },
      labourers: [],
      labourersTotal: 0,
      guidances: [],
      guidancesTotal: 0,
      documents: [],
      activities: [],
      labourerColumns: [
        { field: 'name', label: 'Labourer' },
        { field: 'emp_code', label: 'Employee Code' },
        { field: 'phone_number', label: 'Phone' },
        { field: 'status', label: 'Status' }
      ],
      guidanceColumns: [
        { field: 'guidance_number', label: 'Guidance #' },
        { field: 'name', label: 'Name' },
        { field: 'start_date', label: 'Start Date' },
        { field: 'end_date', label: 'End Date' },
        { field: 'status', label: 'Status' }
      ]
    };
  },
  computed: {
    vendorLogoUrl: function() {
      if (this.vendor.logo) {
        return process.env.VUE_APP_IMG_URL + this.vendor.logo;
      }
      return null;
    },
    pageTitle: function() {
      return this.vendor.name || 'Vendor Details';
    },
    pageBreadcrumb: function() {
      return [
        { label: 'Vendors', path: '/vendors' },
        { label: this.vendor.name || 'Details' }
      ];
    },
    pageStatus: function() {
      return {
        label: this.vendor.is_active ? 'Active' : 'Inactive',
        type: this.vendor.is_active ? 'success' : 'danger'
      };
    },
    pageCode: function() {
      return this.vendor.code ? 'Code: ' + this.vendor.code : null;
    },
    pageInfoCards: function() {
      return [
        {
          icon: 'people',
          label: 'Labourers',
          value: this.stats.labourers || 0,
          color: 'primary'
        },
        {
          icon: 'assignment',
          label: 'Guidances',
          value: this.stats.guidances || 0,
          color: 'info'
        },
        {
          icon: 'check_circle',
          label: 'Active Assignments',
          value: this.stats.active_assignments || 0,
          color: 'success'
        },
        {
          icon: 'attach_money',
          label: 'Total Revenue',
          value: this.formatCurrency(this.stats.total_revenue || 0),
          color: 'warning'
        }
      ];
    }
  },
  created: function() {
    this.get();
    this.getStats();
    this.getDocuments();
    this.getActivities();
  },
  methods: {
    async get() {
      try {
        var id = this.$route.params.id;
        await this.$store.dispatch('vendors/get', { id: id });
        this.vendor = this.$store.getters['vendors/vendor'];
        await this.getLabourers();
        await this.getGuidances();
      } catch (e) {
        await this.$store.dispatch('alerts/error', 'Failed to load vendor details');
      }
    },

    async getStats() {
      try {
        var id = this.$route.params.id;
        await this.$store.dispatch('vendors/stats', id);
        var stats = this.$store.getters['vendors/stats'];
        this.stats = Object.assign(this.stats, stats || {});
      } catch (e) {
        console.error('Failed to load stats', e);
      }
    },

    async getLabourers() {
      try {
        var params = {
          filter: { vendor_id: this.vendor.id },
          page: { number: 1, size: 10 }
        };
        await this.$store.dispatch('labourers/list', params);
        this.labourers = this.$store.getters['labourers/list'] || [];
        this.labourersTotal = this.$store.getters['labourers/listTotal'] || 0;
      } catch (e) {
        console.error('Failed to load labourers', e);
      }
    },

    async getGuidances() {
      try {
        var params = {
          filter: { vendor_id: this.vendor.id },
          page: { number: 1, size: 10 }
        };
        await this.$store.dispatch('guidances/list', params);
        this.guidances = this.$store.getters['guidances/list'] || [];
        this.guidancesTotal = this.$store.getters['guidances/listTotal'] || 0;
      } catch (e) {
        console.error('Failed to load guidances', e);
      }
    },

    async getDocuments() {
      try {
        var params = {
          filter: {
            attachment_id: this.$route.params.id,
            attachment_type: 'Vendor'
          }
        };
        await this.$store.dispatch('attachments/list', params);
        this.documents = this.$store.getters['attachments/list'] || [];
      } catch (e) {
        console.error('Failed to load documents', e);
      }
    },

    async getActivities() {
      try {
        var params = { vendor_id: this.$route.params.id };
        await this.$store.dispatch('vendors/activities', params);
        this.activities = this.$store.getters['vendors/activities'] || [];
      } catch (e) {
        console.error('Failed to load activities', e);
      }
    },

    onLabourerSearch: function(query) {
      // Handle search
      console.log('Searching labourers:', query);
    },

    onGuidanceSearch: function(query) {
      // Handle search
      console.log('Searching guidances:', query);
    },

    async toggleVendorStatus() {
      var isActive = this.vendor.is_active;
      var action = isActive ? 'deactivate' : 'activate';
      var actionPast = isActive ? 'deactivated' : 'activated';

      var result = await Swal.fire({
        title: (isActive ? 'Deactivate' : 'Activate') + ' Vendor?',
        html: '<p>Are you sure you want to <strong>' + action + '</strong> this vendor?</p>' +
            '<p style="color: #666; font-size: 14px;">' +
            'Vendor: <strong>' + this.vendor.name + '</strong></p>',
        icon: isActive ? 'warning' : 'question',
        showCancelButton: true,
        confirmButtonColor: isActive ? '#f44336' : '#4caf50',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Yes, ' + action + ' it!',
        cancelButtonText: 'Cancel'
      });

      if (!result.value) return;

      try {
        this.vendor.is_active = !isActive;
        await this.$store.dispatch('vendors/update', this.vendor);

        await Swal.fire({
          title: 'Success!',
          text: 'Vendor has been ' + actionPast + ' successfully.',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false
        });

        this.$store.dispatch('alerts/success', 'Vendor ' + actionPast + ' successfully');
      } catch (e) {
        this.vendor.is_active = isActive;
        this.$store.dispatch('alerts/error', 'Failed to update vendor status');
      }
    },

    viewLabourer(id) {
      this.$router.push({ name: 'View Labourer', params: { id: id } });
    },

    viewGuidance(id) {
      this.$router.push({ name: 'View Guidance', params: { id: id } });
    },

    goToEdit(id) {
      this.$router.push({ name: 'Edit Vendor', params: { id: id } });
    },

    goBack() {
      this.$router.push({ name: 'Vendors List' });
    },

    triggerFileUpload() {
      this.$refs.fileInput.click();
    },

    async onFileChange(event) {
      var file = event.target.files[0];
      if (!file) return;

      try {
        await this.$store.dispatch('attachments/upload', {
          item: { id: this.vendor.id, type: 'vendors' },
          image: file
        });
        this.$store.dispatch('alerts/success', 'Document uploaded successfully');
        await this.getDocuments();
      } catch (e) {
        this.$store.dispatch('alerts/error', 'Failed to upload document');
      }
    },

    async deleteDocument(doc) {
      var result = await Swal.fire({
        title: 'Delete Document?',
        text: 'Are you sure you want to delete this document?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete',
        cancelButtonText: 'Cancel',
        confirmButtonColor: '#f44336'
      });

      if (result.value) {
        try {
          await this.$store.dispatch('attachments/destroy', doc.id);
          this.$store.dispatch('alerts/success', 'Document deleted');
          await this.getDocuments();
        } catch (e) {
          this.$store.dispatch('alerts/error', 'Failed to delete document');
        }
      }
    },

    previewDocument(doc) {
      window.open(process.env.VUE_APP_IMG_URL + doc.path, '_blank');
    },

    getInitials(name) {
      if (!name) return '?';
      var parts = name.split(' ');
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    },

    getDocumentIcon(type) {
      if (!type) return 'insert_drive_file';
      if (type.includes('pdf')) return 'picture_as_pdf';
      if (type.includes('image')) return 'image';
      if (type.includes('word')) return 'description';
      if (type.includes('excel') || type.includes('sheet')) return 'table_chart';
      return 'insert_drive_file';
    },

    getActivityIcon(type) {
      var icons = {
        created: 'add_circle',
        updated: 'edit',
        deleted: 'delete',
        status: 'swap_horiz',
        info: 'info'
      };
      return icons[type] || 'info';
    },

    getLabourerStatusBadge(status) {
      var badges = {
        'Active': 'success',
        'Terminated': 'danger',
        'Suspended': 'warning'
      };
      return badges[status] || 'default';
    },

    getGuidanceStatusBadge(status) {
      var badges = {
        'Accepted': 'success',
        'Approved': 'success',
        'Active': 'success',
        'Reviewed': 'warning',
        'Draft': 'default',
        'Rejected': 'danger'
      };
      return badges[status] || 'default';
    },

    formatDate(date) {
      if (!date) return 'N/A';
      return moment(date).format('DD MMM YYYY');
    },

    formatDateTime(date) {
      if (!date) return 'N/A';
      return moment(date).format('DD MMM YYYY, HH:mm');
    },

    formatCurrency(value) {
      if (!value) return 'TZS 0';
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'TZS',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(value);
    },

    formatNumber(value) {
      if (!value) return '0';
      return new Intl.NumberFormat().format(value);
    },

    formatFileSize(bytes) {
      if (!bytes) return '0 B';
      var sizes = ['B', 'KB', 'MB', 'GB'];
      var i = Math.floor(Math.log(bytes) / Math.log(1024));
      return (bytes / Math.pow(1024, i)).toFixed(2) + ' ' + sizes[i];
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@/assets/scss/md/_variables.scss';
/* ============ TAB CONTENT ============ */
.tab-content {
  padding: 0;
}

/* ============ PROFILE SECTION ============ */
.profile-section {
  margin-bottom: 24px;
}

.profile-card {
  position: relative;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
}

.profile-cover {
  height: 120px;
  background: linear-gradient(135deg, $brand-primary 0%, $brand-primary-dark 100%);
}

.profile-content {
  padding: 0 30px 24px;
  position: relative;
}

.profile-avatar-wrapper {
  display: flex;
  justify-content: center;
  margin-top: -60px;
  margin-bottom: 16px;
}

.profile-avatar {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  border: 5px solid white;
  overflow: hidden;
  background: white;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.avatar-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 42px;
  font-weight: 700;
  color: $brand-primary;
  background: linear-gradient(135deg, #e8eaf6 0%, #f0f0ff 100%);
}

.profile-info {
  text-align: center;
}

.profile-name {
  font-size: 24px;
  font-weight: 700;
  color: $text-primary;
  margin: 0 0 6px;
}

.profile-business {
  font-size: 14px;
  color: $text-muted;
  margin: 0 0 16px;
}

.profile-badges {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 10px;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 5px 14px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;

  .status-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
  }

  &.active {
    background: #e8f5e9;
    color: #2e7d32;

    .status-dot {
      background: #4caf50;
    }
  }

  &.inactive {
    background: #ffebee;
    color: #c62828;

    .status-dot {
      background: #f44336;
    }
  }
}

.code-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 14px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  background: #e3f2fd;
  color: #1565c0;

  .md-icon {
    font-size: 14px !important;
    width: 14px !important;
    height: 14px !important;
  }
}

/* ============ DETAILS GRID ============ */
.details-grid {
  //display: grid;
  //grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 20px;
  margin-bottom: 24px;
}

.detail-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  }
}

.detail-card-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 20px;
  border-bottom: 2px solid $border-color;
  background: #fafbfc;

  .md-icon {
    color: $brand-primary;
    font-size: 22px !important;
    width: 22px !important;
    height: 22px !important;
  }

  h3 {
    margin: 0;
    font-size: 15px;
    font-weight: 600;
    color: $text-primary;
  }
}

.detail-card-body {
  padding: 16px 20px;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #f5f5f5;

  &:last-child {
    border-bottom: none;
  }
}

.detail-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: $text-muted;
  font-weight: 500;

  .md-icon {
    font-size: 16px !important;
    width: 16px !important;
    height: 16px !important;
    color: $text-muted;
  }
}

.detail-value {
  font-size: 13px;
  color: $text-primary;
  font-weight: 600;
  text-align: right;
  max-width: 60%;

  a {
    //color: $brand-primary;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
}

/* ============ STATISTICS ============ */
.stat-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }
}

.stat-item {
  text-align: center;
  padding: 12px 8px;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 3px solid $brand-primary;
}

.stat-value {
  display: block;
  font-size: 20px;
  font-weight: 700;
  color: $text-primary;
  margin-bottom: 4px;
}

.stat-label {
  display: block;
  font-size: 11px;
  color: $text-muted;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
}

/* ============ DESCRIPTION ============ */
.description-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}

.description-text {
  font-size: 14px;
  line-height: 1.7;
  color: $text-secondary;
  margin: 0;
}

/* ============ DOCUMENTS ============ */
.documents-section {
  padding: 20px;
}

.documents-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;

  h4 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: $text-primary;
  }
}

.documents-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
}

.document-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: white;
  border: 1px solid $border-color;
  border-radius: 10px;
  transition: all 0.2s ease;

  &:hover {
    border-color: $brand-primary;
    box-shadow: 0 4px 12px rgba($brand-primary, 0.1);
  }
}

.document-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 10px;
  background: #e8eaf6;
  color: $brand-primary;

  .md-icon {
    font-size: 24px !important;
    width: 24px !important;
    height: 24px !important;
  }
}

.document-info {
  flex: 1;
  min-width: 0;
}

.document-name {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: $text-primary;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.document-meta {
  display: block;
  font-size: 11px;
  color: $text-muted;
  margin-top: 3px;
}

.document-actions {
  display: flex;
  gap: 4px;
}

/* ============ ACTIVITY TIMELINE ============ */
.activity-timeline {
  padding: 20px;
  position: relative;
}

.timeline-item {
  display: flex;
  gap: 16px;
  padding: 16px 0;
  position: relative;

  &:not(:last-child)::before {
    content: '';
    position: absolute;
    left: 19px;
    top: 56px;
    bottom: -16px;
    width: 2px;
    background: $border-color;
  }
}

.timeline-marker {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  flex-shrink: 0;
  z-index: 1;

  .md-icon {
    font-size: 20px !important;
    width: 20px !important;
    height: 20px !important;
  }

  &.created {
    background: #e8f5e9;
    color: #2e7d32;
  }

  &.updated {
    background: #fff3e0;
    color: #e65100;
  }

  &.deleted {
    background: #ffebee;
    color: #c62828;
  }

  &.status {
    background: #e3f2fd;
    color: #1565c0;
  }

  &.info {
    background: #f3e5f5;
    color: #6a1b9a;
  }
}

.timeline-content {
  flex: 1;
  padding-top: 4px;
}

.timeline-title {
  font-size: 14px;
  font-weight: 600;
  color: $text-primary;
  margin-bottom: 4px;
}

.timeline-description {
  font-size: 13px;
  color: $text-secondary;
  margin-bottom: 8px;
}

.timeline-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  font-size: 12px;
  color: $text-muted;
}

.timeline-time,
.timeline-user {
  display: flex;
  align-items: center;
  gap: 4px;

  .md-icon {
    font-size: 14px !important;
    width: 14px !important;
    height: 14px !important;
  }
}

/* ============ EMPTY STATE ============ */
.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: $text-muted;

  .md-icon {
    font-size: 56px !important;
    width: 56px !important;
    height: 56px !important;
    color: #ddd;
    margin-bottom: 12px;
  }

  p {
    margin: 0;
    font-size: 14px;
  }
}

.text-muted {
  color: $text-muted;
  text-align: center;
  padding: 20px;
}

/* ============ RESPONSIVE ============ */
@media (max-width: 768px) {
  .profile-content {
    padding: 0 16px 20px;
  }

  .profile-avatar {
    width: 90px;
    height: 90px;
  }

  .avatar-placeholder {
    font-size: 32px;
  }

  .profile-name {
    font-size: 20px;
  }

  //.details-grid {
  //  grid-template-columns: 1fr;
  //}

  .detail-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
  }

  .detail-value {
    text-align: left;
    max-width: 100%;
  }

  .documents-grid {
    grid-template-columns: 1fr;
  }

  .activity-timeline {
    padding: 16px;
  }
}
</style>